import React, { useState, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import './MediumStyles.css';
import '../Styles/Home.css';
import {
  extractMediumPostImage,
  getMediumPostTitle,
} from '../utils/mediumPosts';

function MediumPostImage({ post, imgSrc }) {
  const [isVisible, setIsVisible] = useState(Boolean(imgSrc));
  const title = getMediumPostTitle(post);

  if (!isVisible || !imgSrc) return null;

  return (
    <a href={post.link} target="_blank" rel="noopener noreferrer">
      <img src={imgSrc} alt={title} onError={() => setIsVisible(false)} />
    </a>
  );
}

export default function MediumPosts({ posts, allPosts }) {
  const { t } = useTranslation();

  const [isLoaded, setIsLoaded] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [sortOrder, setSortOrder] = useState('newest');

  // ✅ 🔥 改動 1：固定每頁數量（避免頁數跳動）
  const [postsPerPage] = useState(6);

  // ✅ 排序邏輯
  const totalPosts = useMemo(() => {
    const sourcePosts = allPosts?.length ? allPosts : posts || [];

    return [...sourcePosts].sort((a, b) => {
      const dateA = new Date(a.pubDate).getTime();
      const dateB = new Date(b.pubDate).getTime();

      return sortOrder === 'oldest' ? dateA - dateB : dateB - dateA;
    });
  }, [allPosts, posts, sortOrder]);

  const totalPages = Math.ceil(totalPosts.length / postsPerPage);

  const currentPosts = totalPosts.slice(
    currentPage * postsPerPage,
    (currentPage + 1) * postsPerPage
  );

  const hasNextPage = currentPage < totalPages - 1;

  // ✅ 載入動畫
  useEffect(() => {
    const timer = setTimeout(() => {
      setIsLoaded(true);
    }, 100);

    return () => clearTimeout(timer);
  }, []);

  // ✅ 🔥 改動 2：當 totalPages 改變時，確保 currentPage 不超界
  useEffect(() => {
    setCurrentPage((page) => Math.min(page, Math.max(totalPages - 1, 0)));
  }, [totalPages]);

  // ✅ 🔥 改動 3：使用 functional update（避免 state 不同步）
  const handleNextPage = () => {
    setCurrentPage((prev) => (prev < totalPages - 1 ? prev + 1 : prev));
  };

  const handlePrevPage = () => {
    setCurrentPage((prev) => (prev > 0 ? prev - 1 : prev));
  };

  const handleSortChange = (event) => {
    setSortOrder(event.target.value);

    // ✅ 🔥 改動 4：排序時重置頁數
    setCurrentPage(0);
  };

  return (
    <div
      className={`medium-posts-container ${
        isLoaded ? 'content-loaded' : 'content-hidden'
      }`}
    >
      <div className="medium-posts-toolbar">
        <label htmlFor="medium-post-sort" className="medium-posts-sort-label">
          {t('articleSort.label', '排序')}
        </label>

        <select
          id="medium-post-sort"
          className="medium-posts-sort-select"
          value={sortOrder}
          onChange={handleSortChange}
        >
          <option value="oldest">
            {t('articleSort.oldestFirst', '日期：舊到新')}
          </option>
          <option value="newest">
            {t('articleSort.newestFirst', '日期：新到舊')}
          </option>
        </select>
      </div>

      <div className="medium-posts">
        {currentPosts.map((post, index) => {
          const imgSrc = extractMediumPostImage(post);
          const title = getMediumPostTitle(post);

          return (
            <div
              className={`post-card ${
                isLoaded ? 'fade-in-element delay-' + ((index % 5) + 1) : ''
              }`}
              key={post.guid}
            >
              <div className="post-card-thumb">
                <MediumPostImage post={post} imgSrc={imgSrc} />
              </div>

              <div className="post-card-body">
                <h3>
                  <a href={post.link} target="_blank" rel="noopener noreferrer">
                    {title}
                  </a>
                </h3>

                <p className="post-author">By {post.author}</p>

                <p className="post-date">
                  {new Date(post.pubDate).toLocaleDateString()}
                </p>
              </div>
            </div>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="pagination-controls">
          <button
            onClick={handlePrevPage}
            disabled={currentPage === 0}
            className="pagination-btn"
          >
            {t('pagination.previous', '上一頁')}
          </button>

          <span className="page-info">
            {t('pagination.pageInfo', '第 {{current}} 頁，共 {{total}} 頁', {
              current: currentPage + 1,
              total: totalPages,
            })}
          </span>

          <button
            onClick={handleNextPage}
            disabled={!hasNextPage}
            className="pagination-btn"
          >
            {t('pagination.next', '下一頁')}
          </button>
        </div>
      )}
    </div>
  );
}
